import { BufferGeometry, Color, Float32BufferAttribute, Mesh, ShaderMaterial } from 'three';
import { noise3 } from './random';
import { COMMON_GLSL } from './seabedShaders';

// Suelo marino: malla de terreno (plano XZ, y = altura) con dunas, vista en perspectiva rasante. Las alturas se
// calculan UNA vez en CPU (heightAt, que usan también las rocas para apoyarse). En el shader: ondulaciones de
// arena (perturban la normal), luz desde la superficie, reflejos (caustics) y niebla con la distancia.

const Z_NEAR = 1350, Z_FAR = -7200;     // extensión en profundidad (mundo)

const VERT = /* glsl */`
  varying vec3 vWorld;
  varying vec3 vN;
  void main() {
    vN = normalize(mat3(modelMatrix) * normal);
    vec4 w = modelMatrix * vec4(position, 1.0);
    vWorld = w.xyz;
    gl_Position = projectionMatrix * viewMatrix * w;
  }
`;

const FRAG = /* glsl */`
  precision highp float;
  uniform float uCausticTime, uCausticScale, uCausticStrength, uRipple, uFrontFill;
  uniform vec3 uSandLit, uSandDark, uSurfaceLight, uCausticColor;
  varying vec3 vWorld;
  varying vec3 vN;
  ${COMMON_GLSL}

  void main() {
    vec3 n = normalize(vN);
    float dist = distance(cameraPosition, vWorld);
    float near = 1.0 - smoothstep(1500.0, 3800.0, dist);              // detalle fino solo cerca (evita aliasing)

    // Ondulaciones de arena: crestas paralelas a X, algo irregulares → perturban la normal.
    #ifdef SAND_RIPPLE
    float rz = vWorld.z * 0.05 + 5.0 * vnoise(vec3(vWorld.x * 0.0028, 0.0, vWorld.z * 0.006));
    n = normalize(n + vec3(0.0, 0.0, -sin(rz) * uRipple * near));
    #endif

    vec3 L = normalize(vec3(-0.3, 1.0, -0.25));
    float dif = max(dot(n, L), 0.0);
    float pool = lightPool(vWorld);                                    // haces de luz grandes (dramatismo)
    pool = max(pool, uFrontFill * (1.0 - smoothstep(1700.0, 3600.0, dist))); // relleno: el frente cercano nunca se hunde en sombra
    // Sombra muy oscura fuera de los haces; dentro, arena bien iluminada.
    vec3 col = mix(uSandDark * 0.55, uSandLit * 2.0, smoothstep(0.1, 1.0, dif) * pool);
    col += uSurfaceLight * 0.12 * dif * pool;

    // Reflejos de la superficie: sutiles y solo dentro de los haces de luz.
    float c = caustic(vWorld.xz / uCausticScale, uCausticTime);
    col += uCausticColor * c * uCausticStrength * pool * (0.25 + 0.75 * near) * smoothstep(0.3, 1.0, n.y);

    col = applyFog(col, dist);
    gl_FragColor = vec4(col, 1.0);
  }
`;

export default class SeabedTerrain {
  // opts: { rng, halfX, uniforms, defines, settings, seed }
  constructor(opts) {
    const { halfX, settings: s } = opts;
    const seed = opts.seed;
    const amp = s.duneAmp, sc = s.duneScale;

    // Dunas alargadas en X (crestas paralelas a la pantalla): ruido estirado en X, y creciendo con la distancia
    // para que las lejanas sigan teniendo presencia tras la perspectiva.
    this.heightAt = (x, z) => {
      const far = 1 + Math.max(0, 1500 - z) / 3200;                   // z=0 → ~1.5 ; z=-7000 → ~3.7
      const n = 0.62 * noise3(x * 0.00055 * sc, seed, z * 0.0011 * sc)
        + 0.28 * noise3(x * 0.0013 * sc + 9.1, seed + 3, z * 0.0026 * sc)
        + 0.10 * noise3(x * 0.004 * sc + 3.7, seed + 7, z * 0.007 * sc);
      return n * amp * far * 0.8;
    };

    const NX = opts.mobile ? 80 : 170, NZ = opts.mobile ? 100 : 170;
    const pos = new Float32Array((NX + 1) * (NZ + 1) * 3);
    let o = 0;
    for (let j = 0; j <= NZ; j++) {
      const z = Z_NEAR + (Z_FAR - Z_NEAR) * Math.pow(j / NZ, 1.6);   // más denso cerca de la cámara
      for (let i = 0; i <= NX; i++) {
        const v = (i / NX) * 2 - 1;
        const x = Math.sign(v) * Math.pow(Math.abs(v), 1.4) * halfX;  // más denso en el centro
        pos[o++] = x; pos[o++] = this.heightAt(x, z); pos[o++] = z;
      }
    }
    const idx = [];
    for (let j = 0; j < NZ; j++) {
      for (let i = 0; i < NX; i++) {
        const a = j * (NX + 1) + i, b = a + 1, c = a + NX + 1, d = c + 1;
        idx.push(a, b, c, b, d, c);
      }
    }
    const geo = new BufferGeometry();
    geo.setAttribute('position', new Float32BufferAttribute(pos, 3));
    geo.setIndex(idx);
    geo.computeVertexNormals();

    const mat = new ShaderMaterial({
      defines: opts.defines,
      uniforms: {
        ...opts.uniforms,
        uSandLit: { value: new Color(s.sandLit) },
        uSandDark: { value: new Color(s.sandDark) },
        uSurfaceLight: { value: new Color(s.surfaceLight) },
        uRipple: { value: s.ripple },
        uFrontFill: { value: s.frontFill },
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
    });
    this.mesh = new Mesh(geo, mat);
    this.mesh.frustumCulled = false;
  }

  dispose() {
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
  }
}
